import React from "react";
import { Loader2, Package } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useAppBookAssets } from "@/hooks/useAppBook";

interface Props {
  orgUnitId: string;
}

function formatRelative(epochSeconds?: number): string {
  if (!epochSeconds) return "—";
  const ms = epochSeconds * 1000;
  const diffMin = Math.round((Date.now() - ms) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
}

export function AppBookAssetsTable({ orgUnitId }: Props) {
  const { data, isLoading, error, refetch } = useAppBookAssets(orgUnitId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading apps…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center">
        <p className="text-sm text-destructive">Failed to load apps</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-2 text-xs text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center">
        <p className="text-sm text-muted-foreground">
          No apps are associated with this organisation unit yet.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card/50">
      <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
        <div className="col-span-5">App</div>
        <div className="col-span-5">Licenses</div>
        <div className="col-span-2 text-right">Last synced</div>
      </div>
      <ul className="divide-y">
        {data.map((asset) => {
          const used = Math.max(0, asset.totalLicenses - asset.availableLicenses);
          const pct =
            asset.totalLicenses > 0 ? Math.round((used / asset.totalLicenses) * 100) : 0;
          return (
            <li key={asset.id} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                {asset.iconUrl ? (
                  <img
                    src={asset.iconUrl}
                    alt={asset.appName}
                    className="h-8 w-8 rounded-md object-cover flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div
                    role="img"
                    aria-label={`${asset.appName} (no icon available)`}
                    className="h-8 w-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0"
                  >
                    <Package aria-hidden className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{asset.appName}</p>
                  <p className="text-xs text-muted-foreground">Adam ID: {asset.adamId}</p>
                </div>
              </div>
              <div className="col-span-5">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">
                    {used} / {asset.totalLicenses} used
                  </span>
                  <span className="text-muted-foreground">
                    {asset.availableLicenses} available
                  </span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
              <div className="col-span-2 text-right text-xs text-muted-foreground">
                {formatRelative(asset.lastSyncedAt)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
